import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, addDoc, collection } from "firebase/firestore";
import { db } from "../firebase";
import { auth } from "../firebase";
import BackButton from "../components/BackButton";
import grayBackground from "../images/gray-background.png";

function VideoOverview() {
  const { videoId } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [videoLoadError, setVideoLoadError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Track video access
  const trackVideoAccess = async () => {
    try {
      console.log("Tracking video access for videoId:", videoId);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No authenticated user found");
        return;
      }

      // Get user data to find organization_id
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Create video access activity record
        const activityData = {
          created_date: new Date(),
          user_id: doc(db, "users", currentUser.uid),
          organization_id: userData.organization_id,
          type: "accessed_video",
          video_id: videoId,
        };

        // Add to user_activity collection
        const activityRef = collection(db, "user_activity");
        await addDoc(activityRef, activityData);
        console.log("Video access tracked successfully");
      }
    } catch (error) {
      console.error("Error tracking video access:", error);
      // Don't throw error - video tracking failure shouldn't prevent video access
    }
  };

  // Handle iframe load events
  const handleIframeLoad = () => {
    console.log("Iframe loaded successfully for video:", videoId);
    setVideoLoaded(true);
    setVideoLoadError(false);
    trackVideoAccess();
  };

  const handleIframeError = () => {
    console.error("Iframe failed to load for video:", videoId);
    setVideoLoadError(true);
    setVideoLoaded(false);
  };

  // Load video from Firebase
  useEffect(() => {
    const loadVideo = async () => {
      try {
        console.log("Loading video with ID:", videoId);
        setLoading(true);
        setError("");

        // Fetch video from Firebase
        const videoDoc = await getDoc(doc(db, "videos", videoId));

        if (videoDoc.exists()) {
          const videoData = videoDoc.data();
          console.log(
            "Video found:",
            videoData.title,
            "URL:",
            videoData.video_url
          );
          setVideo({ id: videoId, ...videoData });
        } else {
          console.error("Video not found for ID:", videoId);
          setError("Video not found");
        }
      } catch (error) {
        console.error("Error loading video:", error);
        setError("Failed to load video");
      } finally {
        setLoading(false);
      }
    };

    if (videoId) {
      loadVideo();
    }
  }, [videoId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading video...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-8">
              <p className="text-gray-600">Video not found.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <BackButton useHistory={true} />
          {/* Title */}
          <h1 className="text-4xl font-bold text-gray-900 mb-4 mt-4">
            {video.title || "No title"}
          </h1>
          {/* Video Player */}
          {video.video_url ? (
            <div className="flex justify-center mb-5">
              <div className="w-full max-w-[90vw]">
                {videoLoadError ? (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-center">
                    <p className="font-semibold">Video failed to load</p>
                    <p className="text-sm mt-1">
                      The video URL may be invalid or the video may not be
                      available for embedding.
                    </p>
                    <p className="text-xs mt-2 text-gray-600">
                      URL: {video.video_url}
                    </p>
                    <button
                      onClick={() => {
                        setVideoLoadError(false);
                        setVideoLoaded(false);
                      }}
                      className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <iframe
                      src={video.video_url}
                      width="100%"
                      height="400"
                      className="w-full h-[200px] md:h-[400px] lg:h-[500px] xl:h-[500px] rounded-lg"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title={video.title || "Video"}
                      onLoad={handleIframeLoad}
                      onError={handleIframeError}
                    ></iframe>
                    {!videoLoaded && !videoLoadError && (
                      <div className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center">
                        <div className="text-center">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                          <p className="mt-2 text-gray-600">Loading video...</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">No video URL available.</p>
            </div>
          )}
          {/* Description and Topics Section */}
          <div
            className="rounded-lg p-8 mt-6"
            style={{
              backgroundImage: `url(${grayBackground})`,
              backgroundRepeat: "repeat",
              backgroundSize: "auto",
            }}
          >
            {/* Description */}
            <p className="text-gray-700 text-md leading-relaxed mb-5">
              {video.description || "No description available"}
            </p>

            {/* Topics */}
            {video.units &&
              Array.isArray(video.units) &&
              video.units.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {video.units.map((unit, index) => (
                    <span
                      key={index}
                      className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {unit}
                    </span>
                  ))}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoOverview;
